import { Flex } from 'antd';
import Button from 'components/Button';
import { useCallback, useMemo, useState } from 'react';
import PendingInvoiceTable from './PendingInvoiceTable';

import SearchBox from 'components/SearchBox';
import UtilitySummary from 'screens/system-utils/shared/components/UtilitySummary';

import usePendingInvoiceListQuery from '../hooks/usePendingInvoiceListQuery';
import useSimulatePaymentMutation from '../hooks/useSimulatePaymentMutation';

import useMessage from 'antd/es/message/useMessage';
import { copyToClipboard } from 'utils/helpers';

const PendingInvoices = () => {
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [message, contextHolder] = useMessage();

  const params = useMemo(() => {
    return {
      'description.contains': searchTerm as string,
    };
  }, [searchTerm]);

  const {
    data: pendingInvoiceList,
    isLoading,
    isFetching,
    total,
    currentPage,
    pageSize,
    handlePageChange,
    updateInvoiceAmount,
    refetch,
  } = usePendingInvoiceListQuery(params);

  const { mutate } = useSimulatePaymentMutation({
    onMutate: handleSimulatePaymentMutation,
    onSettled: handleSimulatePaymentSettled,
  });

  function handleSimulatePaymentMutation() {
    message.destroy();
    message.loading({
      content: 'Simulating payment',
      key: 'simulate-payment',
    });
  }
  function handleSimulatePaymentSettled(
    data: unknown,
    error: Error | null,
    variables: { invoiceId: string; amount: number },
  ) {
    message.destroy('simulate-payment');
    if (error) {
      message.error({
        content: error?.message || 'Something went wrong',
        key: 'simulate-payment-error',
      });
      return;
    }

    if (data) {
      const invoiceId = variables.invoiceId;
      let msg = `Successfully simulated payment for invoice ${invoiceId}, We have copied the invoice id to your clipboard. Please import it via "Import Invoice" utility after some time.`;
      copyToClipboard(invoiceId)
        .catch(() => {
          msg = `Successfully simulated payment for invoice ${invoiceId}, We could not copy the invoice id to your clipboard. Please type it manually and import it via "Import Invoice" utility after some time.`;
        })
        .finally(() => {
          message.success({
            content: msg,
            key: 'simulate-payment-success',
            duration: 3,
          });
        });
    }
  }

  const handlePaymentSimulation = useCallback(
    (invoiceId: string, amount: number, externalId: string) => {
      if (!invoiceId || !amount) return;

      mutate(
        {
          invoiceId,
          amount,
          externalId,
        },
        {
          onSettled: () => {
            refetch();
          },
        },
      );
    },
    [pendingInvoiceList],
  );

  return (
    <Flex vertical gap={16}>
      {contextHolder}
      <Flex justify="space-between" align="center" wrap="wrap">
        <UtilitySummary
          title="List of pending invoices"
          description="Invoices for which bank transfer has been initiated and an active VA exist."
        />
        <Flex gap={16} align="center" wrap="wrap">
          <SearchBox
            onSearch={(v) => setSearchTerm(v)}
            showLabel={false}
            placeholder="Search with invoice number"
            style={{ width: 300 }}
            disabled={isLoading || isFetching}
          />
          <Button
            type="primary"
            className="d-flex align-items-center"
            onClick={() => refetch}
            loading={isFetching}
          >
            Refresh
          </Button>
        </Flex>
      </Flex>

      <PendingInvoiceTable
        data={pendingInvoiceList}
        isLoading={isLoading || isFetching}
        pagination={{
          total,
          currentPage,
          pageSize,
          handlePageChange,
        }}
        onPaymentSimulateAction={handlePaymentSimulation}
        onPaymentAmountChange={updateInvoiceAmount}
      />
    </Flex>
  );
};

export default PendingInvoices;
